import { useMemo, useState } from 'react';
import { css } from '@emotion/css';
import { Colors } from '../../shared/styles/colors';
import {
  Session,
  Source,
  Socket,
  SessionGroup,
} from '../../shared/types/server-types';
import {
  PowerTypeEnum,
  sourceLabels,
  SourceTypeEnum,
} from '../../shared/types/enums';
import { OutlineX, PowerIcon, WaterIcon } from '../../shared/assets/icons';
import { h2Style, p2Style } from '../../shared/styles/text';
import { useInterval } from '../../shared/custom-hooks/custom-hooks';
import dayjs from 'dayjs';
import { getDurationFormat } from '../../shared/utils/duration';
import { Info } from '../../shared/components/Info';
import { Oval } from 'react-loader-spinner';
import { Motion, Radius, unstyledButton } from '../../shared/styles/tokens';

interface ISessionCardProps {
  session: Session;
  sessionGroup: SessionGroup;
  showVehicle: boolean;
  handleStopSession: (val: Session, sourcePositon: number) => Promise<void>;
  refetchSessions: () => Promise<any>;
}

export const SessionCard = (props: ISessionCardProps): JSX.Element => {
  const {
    session,
    showVehicle,
    refetchSessions,
    handleStopSession,
    sessionGroup,
  } = props;
  const { position: sourcePositon, state } = session.source;
  const { identifier: pedestalId } = session.source.pedestal;
  const [endingSession, setEndingSession] = useState<boolean>(false);
  const diff = dayjs(session.endedAt || undefined).diff(
    dayjs(session.createdAt),
  );
  const durr = dayjs.duration(diff);

  const durationFormat = useMemo(() => getDurationFormat(durr), [durr]);
  const [duration, setDuration] = useState<string>(durr.format(durationFormat));
  const [delay] = useState<number>(1000);

  useInterval(() => {
    const diff = dayjs(session.endedAt || undefined).diff(
      dayjs(session.createdAt),
    );
    const durr = dayjs.duration(diff);
    setDuration(durr.format(durationFormat));
  }, delay);

  const determineColorStyle = (source: Source) => {
    if (source.type === SourceTypeEnum.SOCKET) {
      if ((source as Socket).phase === PowerTypeEnum.SINGLE)
        return `${Colors.MONO_PHASE}`;
      else return `${Colors.THREE_PHASE}`;
    }
    return `${Colors.CAPRI_BLUE}`;
  };

  const colorStyle = useMemo(() => determineColorStyle(session.source), []);

  const endSession = async () => {
    if (
      !confirm(
        `Are you sure you want to end your ${session.source.type} ${sourcePositon} session?`,
      )
    ) {
      return;
    }
    setEndingSession(true);
    await handleStopSession(session, sourcePositon);
    setEndingSession(false);
    await refetchSessions();
  };

  const calculateTotal = () => {
    const tariff =
      session.source.type === SourceTypeEnum.SOCKET
        ? sessionGroup.account?.powerTariff ||
          sessionGroup.location.constants.powerTariff
        : sessionGroup.account?.waterTariff ||
          sessionGroup.location.constants.waterTariff;
    const consumption = session.source.state.value - session.initialMeterState;
    return (tariff * consumption).toFixed(2);
  };

  const total = useMemo(() => calculateTotal(), [sessionGroup]);

  return (
    <div className={sessionCardStyle(colorStyle)}>
      <div className={sessionCardHeaderStyle(colorStyle)}>
        <div>
          <p>{pedestalId.toUpperCase()}</p>
          <h2>
            {sourceLabels[session.source.type]} {sourcePositon}
          </h2>
        </div>
        {session.source.type === SourceTypeEnum.SOCKET && <PowerIcon />}
        {session.source.type === SourceTypeEnum.VALVE && <WaterIcon />}
      </div>
      <div className={sessionInfoSectionStyle}>
        {session.source.type === SourceTypeEnum.SOCKET && (
          <Info
            label="consumption"
            value={`${(state.value - session.initialMeterState).toFixed(
              3,
            )} kWh`}
          />
        )}
        {session.source.type === SourceTypeEnum.VALVE && (
          <Info
            label="consumption"
            value={`${(state.value - session.initialMeterState).toFixed(3)} m³`}
          />
        )}
        <Info label="duration" value={duration} />
        <Info label="total" value={`${total} EUR`} />
        {showVehicle && (
          <Info label="vehicle" value={session.vehicle.registration} />
        )}
      </div>
      <button
        type="button"
        onClick={endSession}
        className={closeButtonStyle(colorStyle)}
        disabled={endingSession}
        aria-label={`End ${sourceLabels[session.source.type]} ${sourcePositon} session`}
        title="End session"
      >
        {endingSession ? (
          <Oval
            height={22}
            width={22}
            color={'white'}
            secondaryColor="rgba(255,255,255,0.4)"
            strokeWidth={5}
          />
        ) : (
          <OutlineX />
        )}
      </button>
    </div>
  );
};

const sessionCardStyle = (colorStyle: string) => css`
  position: relative;
  width: 100%;
  border: 1.5px solid ${colorStyle}55;
  background-color: ${colorStyle}14;
  border-radius: ${Radius.lg};
  padding: 1rem 1rem 0.75rem 1rem;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const sessionCardHeaderStyle = (colorStyle: string) => css`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  text-transform: capitalize;

  > div {
    min-width: 0;
  }

  > div > p {
    ${p2Style}
    color: ${Colors.TEXT_SECONDARY};
  }
  > div > h2 {
    ${h2Style}
    overflow-wrap: anywhere;
  }

  > svg {
    width: 3rem;
    height: 3rem;
    flex: 0 0 auto;
    align-self: center;

    path {
      fill: ${colorStyle};
    }
  }
`;

const sessionInfoSectionStyle = css`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  margin: 0px;
  padding-right: 3rem;
`;

const closeButtonStyle = (colorStyle: string) => css`
  ${unstyledButton}
  position: absolute;
  bottom: 0px;
  right: 0px;
  background-color: ${colorStyle};
  width: 2.75rem;
  height: 2.75rem;
  border-top-left-radius: ${Radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    filter ${Motion.fast},
    transform ${Motion.fast};

  > svg {
    display: block;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      filter: brightness(0.92);
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    cursor: progress;
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: -4px;
  }
`;
